import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Available language options for the testing panel.
 */
export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'dev', label: 'Dev Mode', flag: '🔑' },
] as const

export type LanguageCode = (typeof LANGUAGES)[number]['code']

/**
 * Hook encapsulating the language-switching logic for LanguageSelectorPanel.
 *
 * Handles dev-mode translation key injection and language changes via i18next.
 * Exposes safe synchronous callbacks compatible with RadioGroup.onChange.
 *
 * @returns languages - Static list of available language options
 * @returns selectedLanguage - Currently active language code
 * @returns handleLanguageChange - Synchronous handler to trigger a language switch
 * @returns isDevMode - Whether dev mode (translation keys as labels) is active
 *
 * @example
 * ```ts
 * const { languages, selectedLanguage, handleLanguageChange, isDevMode } = useLanguageSelectorPanel()
 * ```
 */
export const useLanguageSelectorPanel = () => {
  const { i18n } = useTranslation()

  const selectedLanguage = (i18n.language || 'en') as LanguageCode

  const changeToDevMode = useCallback(async (): Promise<void> => {
    const currentLang = i18n.language
    const currentTranslations = i18n.getResourceBundle(currentLang, 'translation')

    if (currentTranslations) {
      const debugTranslations: Record<string, string> = {}
      Object.keys(currentTranslations).forEach(key => {
        debugTranslations[key] = key
      })
      i18n.addResourceBundle('dev', 'translation', debugTranslations, true, true)
      await i18n.changeLanguage('dev')
    }
  }, [i18n])

  const changeToLanguage = useCallback(
    async (langCode: LanguageCode): Promise<void> => {
      const existingTranslations = i18n.getResourceBundle(langCode, 'translation')
      if (existingTranslations) {
        await i18n.changeLanguage(langCode)
      } else {
        // eslint-disable-next-line no-console
        console.warn(`[LanguageSelectorPanel] No translations found for "${langCode}"`)
      }
    },
    [i18n],
  )

  const handleLanguageChange = useCallback(
    (langCode: string): void => {
      if (langCode === 'dev') {
        void changeToDevMode()
      } else {
        void changeToLanguage(langCode as LanguageCode)
      }
    },
    [changeToDevMode, changeToLanguage],
  )

  return {
    languages: LANGUAGES,
    selectedLanguage,
    handleLanguageChange,
    isDevMode: selectedLanguage === 'dev',
  }
}
